import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import _, { at } from 'lodash';
import { getIn, useField, useFormikContext } from 'formik';
import { Box, FormControl, FormHelperText, MenuItem, Select, Typography, TextField, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { bloqcibeApi } from '../../../store/slices/apiSlice';
import { setTempParticipantId } from '../../../store/slices/subjectSlice';
import { useParams } from 'react-router-dom';
import CustomButton from '../../../components/@extended/CustomButton';

function SelectField(props) {
  const {
    label,
    data = [],
    fieldKey,
    valueKey,
    placeholder,
    required,
    requiredDepends,
    requiredValidationValue,
    requiredDependOnFields,
    fieldConfig,
    name,
    enableOther = false,
    ...rest
  } = props;
  let { trialId, trialSiteId, subjectMasterId, crfMasterId } = useParams();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const [field, meta, helpers] = useField(props);
  const { values } = useFormikContext();
  const dispatch = useDispatch();
  const subject = useSelector((state) => state.subject);
  const { value: selectedValue } = field;
  const [touched, error] = at(meta, 'touched', 'error');
  const isError = touched && error && true;
  const [disabled, setDisabled] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const parentRef = useRef(null);
  const [options, setOptions] = useState(data || []);
  const trialSite = useSelector((state) => state.trial?.trialSiteDetail)
  const [getSubjectAnswers] = bloqcibeApi.endpoints.getSubjectAnswers.useLazyQuery();
  const [getTrialSiteInfo] = bloqcibeApi.endpoints.getTrialSiteInfo.useLazyQuery(); 
  const [getVisitAnswers] = bloqcibeApi.endpoints.getVisitAnswers.useLazyQuery();
  const [toggleForOtherOption, setToggleForOtherOption] = useState(false);
  const [otherOption, setOtherOption] = useState('');
  const [selectOpen, setSelectOpen] = useState(false);
  // Ref to track toggle state synchronously (for onClose check)
  const toggleForOtherOptionRef = useRef(false);
  const apiCacheRef = useRef(new Map());
  const siteId = trialSite?.siteTrialData?.siteId;

  const applyValueIfChanged = (nextValue) => {
    if (nextValue !== undefined && nextValue !== null && nextValue !== '' && getIn(values, name) !== nextValue) {
      helpers.setValue(nextValue);
    }
  };

  // Helper to get flat options for duplicate checking
  const flatOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    // Check if options are objects with value property
    const isObjectOptions = options.length > 0 && typeof options[0] === 'object' && options[0] !== null && options[0].hasOwnProperty('value');
    if (isObjectOptions) {
      return options.filter((item) => item?.value).map((item) => item.value);
    }
    return options;
  }, [options]);

  useEffect(() => {
    if (props.disabled) {
      setDisabled(true);
    }
  }, [props.disabled]);
  useEffect(() => {
    (async () => {
      if (!fieldKey) {
        return;
      }

      switch (fieldKey) {
          case 'weight_unit':
          case 'race_demographics':
          case 'ethnicity_demographics':
          case 'height_unit': {
            const value1 = valueKey ? getIn(subject?.answers, valueKey) : '';
            applyValueIfChanged(value1);
            break;
          }
          case 'gender_demographics': {
            const currentValue2 = getIn(values, name);
            if (currentValue2) {
              return;
            }

            const fromSubject = valueKey ? getIn(subject?.answers, valueKey) : '';
            if (fromSubject) {
              applyValueIfChanged(fromSubject);
              return;
            }

            if (!fieldConfig?.dependStepKey) {
              return;
            }
            if (!siteId) {
              return;
            }

            const genderCacheKey = `getSubjectAnswers_${sponsorId}_${trialId}_${siteId}_${subjectMasterId}_${fieldConfig?.dependStepKey}_${fieldConfig?.dependSectionKey}_${fieldConfig?.dependSubSectionKey}_${fieldConfig?.dependCategoryKey}_${fieldConfig?.dependFieldKey}`;
            let answers;
            if (apiCacheRef.current.has(genderCacheKey)) {
              answers = apiCacheRef.current.get(genderCacheKey);
            } else {
              answers = await getSubjectAnswers({
                sponsorId,
                trialId,
                siteId,
                payload: {
                  subjectMasterId: subjectMasterId,
                  stepKey: fieldConfig?.dependStepKey,
                  sectionKey: fieldConfig?.dependSectionKey,
                  subSectionKey: fieldConfig?.dependSubSectionKey,
                  categoryKey: fieldConfig?.dependCategoryKey,
                  fieldKey: fieldConfig?.dependFieldKey,
                },
              }).unwrap();
              apiCacheRef.current.set(genderCacheKey, answers);
            }
            const ans1 = answers?.[fieldConfig?.dependCategoryKey]?.[fieldConfig?.dependFieldKey];
            applyValueIfChanged(ans1);
            break;
          }
          case 'cdst_classification':
            const cdstScore = getIn(values, 'cdst_pdl.cdst_score');
            const cdstClassification = getIn(values, 'cdst_pdl.cdst_classification');

            const bowelSurgery = getIn(values, 'cdst_pdl.cdst_history_of_bowel_surgery');
            const priorTNFAntagonistTherapy = getIn(values, 'cdst_pdl.cdst_history_of_prior_TNF-antagonist_therapy');
            const priorFistulizingDisease = getIn(values, 'cdst_pdl.cdst_history_of_prior_fistulizing_disease');

            const isalbuminAvailable = getIn(values, 'cdst_pdl.cdst_baseline_albumin_available');
            const iscrpAvailable = getIn(values, 'cdst_pdl.cdst_baseline_crp_available');
            if (isalbuminAvailable === 'No' || iscrpAvailable === 'No' || bowelSurgery === 'Unknown' || priorTNFAntagonistTherapy === 'Unknown' || priorFistulizingDisease === 'Unknown') {
              helpers.setValue('Invalid');
              return;
            }

            if (cdstScore !== undefined && cdstScore !== null && cdstScore !== '') {
              let classification = '';
              if (cdstScore <= 13) {
                classification = 'Low';
              } else if (cdstScore > 19) {
                classification = 'High';
              } else if (cdstScore > 13 && cdstScore <= 19) {
                classification = 'Intermediate';
              }
              if (classification) {
                await helpers.setValue(classification);
                if (cdstClassification === '') return;
                await helpers.setTouched(true);
              }
            }
            break;
          default:
            break;
        }
    })();
  }, [
    fieldKey,
    name,
    valueKey,
    siteId,
    sponsorId,
    trialId,
    subjectMasterId,
    subject?.answers,
    fieldConfig?.dependStepKey,
    fieldConfig?.dependSectionKey,
    fieldConfig?.dependSubSectionKey,
    fieldConfig?.dependCategoryKey,
    fieldConfig?.dependFieldKey,
    getIn(values, name),
    getIn(values, "cdst_pdl.cdst_score"),
    getIn(values, "cdst_pdl.cdst_classification"),
    getIn(values, "cdst_pdl.cdst_history_of_bowel_surgery"),
    getIn(values, "cdst_pdl.cdst_history_of_prior_TNF-antagonist_therapy"),
    getIn(values, "cdst_pdl.cdst_history_of_prior_fistulizing_disease"),
    getIn(values, "cdst_pdl.cdst_baseline_albumin_available"),
    getIn(values, "cdst_pdl.cdst_baseline_crp_available"),
    getIn(values, "cdst_pdl.cdst_baseline_albumin"),
    getIn(values, "cdst_pdl.cdst_baseline_crp"),
  ]);

  useEffect(() => {
    const includeConditions = ['immunomodulators_type', 'corticosteroid_type','Small_Molecule_type'];
    if (
      fieldKey &&
      includeConditions.some((cond) => fieldKey.includes(cond))
    ) {
      const fetchBiologicData = async () => {
        const trialSiteData = await getTrialSiteInfo({
          sponsorId: sponsorId,
          trialId: trialId,
          siteTrialId: trialSiteId,
        }).unwrap();

        const _answers = await getVisitAnswers({
          sponsorId: sponsorId,
          trialId: trialId,
          trialSiteId: trialSiteId,
          siteId: trialSiteData?.siteTrialData?.siteId,
          payload: {
            crfMasterId: crfMasterId,
            subjectMasterId: subjectMasterId,
            stepKey: fieldConfig?.dependStepKey,
            sectionKey: fieldConfig?.dependSectionKey,
            subSectionKey: fieldConfig?.dependSubSectionKey,
            categoryKey: fieldConfig?.dependCategoryKey,
          },
        }).unwrap();

        if (_answers && fieldConfig?.dependFieldKeys) {
          let values = fieldConfig?.dependFieldKeys
            .filter((val) => {
              if (val?.valueAsOption) {
                return (
                  _answers[fieldConfig?.dependCategoryKey] &&
                  _answers[fieldConfig?.dependCategoryKey][val?.key] !== undefined &&
                  _answers[fieldConfig?.dependCategoryKey][val?.key] !== null &&
                  _answers[fieldConfig?.dependCategoryKey][val?.key] !== ''
                );
              } else {
                return (
                  _answers[fieldConfig?.dependCategoryKey] &&
                  _answers[fieldConfig?.dependCategoryKey][val?.key] === val?.value
                );
              }
            })
            .map((val) => {
              if (val?.valueAsOption) {
                return _answers[fieldConfig?.dependCategoryKey][val?.key];
              } else {
                return val.key;
              }
            })
            .filter((v) => v !== undefined && v !== null && v !== '');
          setOptions([...(fieldConfig?.options?.values || []), ...values]);
        }
      };

      fetchBiologicData();
    }
  }, [fieldKey, sponsorId, trialId, trialSiteId, crfMasterId, fieldConfig, subjectMasterId]);

  useEffect(() => {
    if ((fieldKey === 'piList' || fieldKey === 'sitePIListKey') && trialSite?.users) {
      const piUsers = _.filter(trialSite.users, (userRole) => userRole.role === 'PI');
      const piOptions =
        piUsers?.map((userRole) => ({
          value: userRole.roleCode,
          label: `${userRole.user?.firstName || ''} ${userRole.user?.lastName || ''}`.trim(),
        })) || [];
      
      setOptions(piOptions);

      const value = getIn(values, 'pdl_Checklist.participantIdKey') || getIn(values, 'demographic_data.participantIdKey');
      
      if (subjectMasterId && value) {
        const piRoleCode = value.includes('-') && value?.split('-')[1];
        const piRole = piUsers.find((userRole) => userRole.roleCode === piRoleCode);
        if (piRole) {
          helpers.setValue(piRole.roleCode);
        }
      } else if(!value && piOptions.length > 0) {
        helpers.setValue(piOptions[0].value);
      }
    }
  }, [fieldKey, trialSite, subjectMasterId, values]);

  useEffect(() => {
    if (fieldKey === 'piList' || fieldKey === 'sitePIListKey') {
      const participantIdValue =
        getIn(values, 'pdl_Checklist.participantIdKey') || getIn(values, 'demographic_data.participantIdKey');
      if (participantIdValue) {
        dispatch(setTempParticipantId(participantIdValue));
      }
    }
  }, [fieldKey, dispatch, values, getIn(values, 'pdl_Checklist.participantIdKey'), getIn(values, 'demographic_data.participantIdKey')]);

  useEffect(() => {
    if (fieldConfig?.options?.valuesDependsOnFields && fieldConfig?.options?.valuesDependsOnFields.length > 0) {
      for (const dep of fieldConfig?.options?.valuesDependsOnFields) {
        if (dep.fieldKey && dep.fieldValue && dep.values) {
          if (getIn(values, dep.fieldKey) === dep.fieldValue) {
            setOptions(dep.values);
            helpers.setValue(dep.values[0] || '');
            break;
          }
        } else {
          setOptions(data || []);
        }
      }
    }
  }, [fieldConfig?.options?.valuesDependsOnFields, values]);
  
  // Handler functions for "Other" option
  const handleSubmitOtherOption = () => {
    const trimmed = otherOption.trim();
    if (!trimmed) return;

    // Check for duplicates (case-insensitive)
    const lowerTrimmed = trimmed.toLowerCase();
    const alreadyExists = flatOptions.some((value) => value && value.toLowerCase() === lowerTrimmed);

    if (alreadyExists) {
      // Find the existing option with correct case and select it
      const existingValue = flatOptions.find((value) => value && value.toLowerCase() === lowerTrimmed);
      helpers.setValue(existingValue);
      setOtherOption('');
      toggleForOtherOptionRef.current = false;
      setToggleForOtherOption(false);
      setSelectOpen(false);
      return;
    }

    // Add new option to options list
    const isObjectOptions = options.length > 0 && typeof options[0] === 'object' && options[0] !== null && options[0].hasOwnProperty('value');
    if (isObjectOptions) {
      const newOption = { value: trimmed, label: trimmed };
      setOptions([...options, newOption]);
    } else {
      setOptions([...options, trimmed]);
    }

    // Set the new value as selected
    helpers.setValue(trimmed);

    // Reset other option state
    setOtherOption('');
    toggleForOtherOptionRef.current = false;
    setToggleForOtherOption(false);
    setSelectOpen(false);
  };

  const handleCancelOtherOption = () => {
    setOtherOption('');
    toggleForOtherOptionRef.current = false;
    setToggleForOtherOption(false);
    setSelectOpen(false);
  };

  const handleOtherOptionChange = (e) => {
    e.stopPropagation();
    setOtherOption(e.target.value);
  };

  const handleOtherOptionKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitOtherOption();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelOtherOption();
    }
  };

  useEffect(() => {
    if (parentRef.current) {
      setMenuWidth(parentRef.current.offsetWidth);
    }
    const handleResize = () => {
      if (parentRef.current) {
        setMenuWidth(parentRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isRequired = useMemo(() => {
    if (Array.isArray(requiredDependOnFields) && requiredDependOnFields.length > 0) {
      for (const dep of requiredDependOnFields) {
        if (dep.requiredDepends) {
          const dependsKey = dep.requiredDepends.includes('.') ? dep.requiredDepends.split('.') : dep.requiredDepends;
          const dependsValue = dep.requiredDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];
          
          // Check if requiredValidationValue is provided - if so, use exact match validation
          // If not provided, check if any value exists (truthy check)
          if (dep.requiredValidationValue !== undefined && dep.requiredValidationValue !== null && dep.requiredValidationValue !== '') {
            if (dependsValue === dep.requiredValidationValue) {
              return true;
            }
          } else {
            // If requiredValidationValue is not provided, check if any value exists
            if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
              return true;
            }
          }
        }
      }
    } else if (requiredDepends) {
      const dependsKey = requiredDepends.includes('.') ? requiredDepends.split('.') : requiredDepends;
      const dependsValue = requiredDepends?.includes('.') ? values[dependsKey[0]]?.[dependsKey[1]] : values[dependsKey];
      
      // Check if requiredValidationValue is provided - if so, use exact match validation
      // If not provided, check if any value exists (truthy check)
      if (requiredValidationValue !== undefined && requiredValidationValue !== null && requiredValidationValue !== '') {
        if (dependsValue === requiredValidationValue) {
          return true;
        }
      } else {
        // If requiredValidationValue is not provided, check if any value exists
        if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
          return true;
        }
      }
    }

    if (required) {
      return true;
    }

    return false;
  }, [values, requiredDepends, requiredValidationValue, required, requiredDependOnFields]);

  const isDisabled = useMemo(() => {
    if (props.disabled) {
      return true;
    }

    if (Array.isArray(props.disabledDependsOnFields) && props.disabledDependsOnFields.length > 0) {
      for (const dep of props.disabledDependsOnFields) {
        if (dep.disabledDepends && dep.disabledValidationValue !== undefined) {
          const dependsKey = dep.disabledDepends.includes('.') ? dep.disabledDepends.split('.') : dep.disabledDepends;
          const dependsValue = dep.disabledDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];
          if (dependsValue === dep.disabledValidationValue) {
            return true;
          }
        }
      }
    } else if (props.disabledDepends && props.disabledValidationValue !== undefined) {
      const dependsKey = props.disabledDepends.includes('.') ? props.disabledDepends.split('.') : props.disabledDepends;
      const dependsValue = props.disabledDepends?.includes('.') ? values[dependsKey[0]]?.[dependsKey[1]] : values[dependsKey];
      if (dependsValue === props.disabledValidationValue) {
        return true;
      }
    }


    if ((fieldKey === 'piList' || fieldKey === 'sitePIListKey') && subjectMasterId) { 
      return true;
    }
    return false;
  }, [
    values,
    props.disabledDepends,
    props.disabledValidationValue,
    props.disabled,
    props.disabledDependsOnFields,
    subjectMasterId,
    fieldKey
  ]);
  
  function _renderHelperText() {
    if (isError) {
      return <FormHelperText>{error}</FormHelperText>;
    } else {
      return <FormHelperText> </FormHelperText>;
    }
  }

  return (
    <Box
      ref={parentRef}
      sx={{
        height: props.justifycontent ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: props.justifycontent ? 'start' : 'space-between',
      }}
    >
      <Typography sx={{ color: '#5b5b5b', fontWeight: 'medium' }} variant="subtitle1" gutterBottom>
        {label}
        {label && isRequired && <span style={{ color: 'red', fontSize: 18 }}>*</span>}
      </Typography>
      <FormControl error={isError}>
        {/* <InputLabel >{label}</InputLabel> */}
        <Select
          displayEmpty
          disabled={isDisabled}
          required={isRequired}
          {...field}
          value={selectedValue ? selectedValue : ''}
          open={selectOpen}
          onOpen={() => setSelectOpen(true)}
          onClose={(event, reason) => {
            // Keep dropdown open when toggling "Other" option (use ref for synchronous check)
            if (toggleForOtherOptionRef.current) {
              return;
            }
            setSelectOpen(false);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                width: menuWidth,
              },
            },
          }}
          renderValue={(selected) => {
            if (selected === '') {
              return <em style={{ color: '#aaa', fontStyle: 'normal' }}>{placeholder ?? label}</em>;
            }
            // Check if options are objects with value and label properties
            const isObjectOptions = options.length > 0 && typeof options[0] === 'object' && options[0] !== null && options[0].hasOwnProperty('value');
            if (isObjectOptions) {
              const selectedOption = options.find(opt => opt && opt.value === selected);
              const displayText = selectedOption?.label || selected;
              return <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{displayText}</Box>;
            }
            return <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{selected}</Box>;
          }}
        >
          <MenuItem value="">
            <em style={{ color: '#aaa', fontStyle: 'normal' }}>
              {placeholder ? placeholder : label ? label : 'Select'}
            </em>
          </MenuItem>
          {options?.map((item, index) => {
            if (!item) return null;
            const isObjectOption = typeof item === 'object' && item !== null && item.hasOwnProperty('value');
            const itemValue = isObjectOption ? item.value : item;
            const itemLabel = isObjectOption ? item.label : item;
            return (
              <MenuItem key={index} value={itemValue} sx={{ whiteSpace: 'normal' }}>
                {itemLabel}
              </MenuItem>
            );
          })}

          {enableOther && toggleForOtherOption ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{ pl: 2, pr: 2, pt: 1, pb: 1 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Enter custom option..."
                value={otherOption}
                onChange={handleOtherOptionChange}
                onKeyDown={handleOtherOptionKeyDown}
                autoFocus
                error={
                  otherOption.trim() &&
                  flatOptions.some((value) => value && value.toLowerCase() === otherOption.trim().toLowerCase())
                }
                helperText={
                  otherOption.trim() &&
                  flatOptions.some((value) => value && value.toLowerCase() === otherOption.trim().toLowerCase())
                    ? 'This option already exists'
                    : ''
                }
              />
              <CustomButton
                sx={{ height: 39, minWidth: 80 }}
                variant="contained"
                onClick={handleSubmitOtherOption}
                disabled={!otherOption.trim()}
              >
                Add
              </CustomButton>
              <CustomButton sx={{ height: 39, minWidth: 80 }} variant="outlined" onClick={handleCancelOtherOption}>
                Cancel
              </CustomButton>
            </Stack>
          ) : enableOther ? (
            <MenuItem
              sx={{
                ':hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                justifyContent: 'flex-start',
                pl: 2,
                color: '#1976d2',
                fontSize: '14px',
                fontStyle: 'italic',
                borderTop: '1px solid #e0e0e0',
                mt: 1,
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Set ref synchronously BEFORE state update (so onClose sees it immediately)
                toggleForOtherOptionRef.current = true;
                setToggleForOtherOption(true);
                setSelectOpen(true);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              + Other (Please specify)
            </MenuItem>
          ) : null}
        </Select>
        {_renderHelperText()}
      </FormControl>
    </Box>
  );
}

SelectField.propTypes = {
  data: PropTypes.array,
  enableOther: PropTypes.bool,
};

export default React.memo(SelectField);
